import React, { useState, useEffect } from 'react';
import { Edit2, Save, X, FileDown } from 'lucide-react';
import { CopyButton } from '../ui/CopyButton';
import { exportToPDF } from '../../utils/pdf/pdfExporter';

interface EditableLetterProps {
  content: string;
  jobDescription: string;
  onSave?: (newContent: string) => Promise<void>;
}

export function EditableLetter({ content, jobDescription, onSave }: EditableLetterProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(content);
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setEditedContent(content);
  }, [content]);

  const handleSave = async () => {
    if (!onSave) return;
    
    setIsSaving(true);
    setError(null);
    
    try {
      await onSave(editedContent);
      setIsEditing(false);
    } catch (err) {
      setError('Failed to save changes. Please try again.');
      console.error('Save error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedContent(content);
    setIsEditing(false);
    setError(null);
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    setError(null);

    try {
      await exportToPDF(content);
    } catch (err) {
      setError('Failed to export PDF. Please try again.');
      console.error('PDF export error:', err);
    } finally {
      setIsExporting(false);
    }
  };

  if (isEditing) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-2">
            {error && (
              <span className="text-red-600 text-sm">{error}</span>
            )}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center space-x-2 text-success hover:text-success-hover transition-colors disabled:opacity-50"
              title="Save changes"
            >
              <Save className="h-4 w-4" />
              <span>{isSaving ? 'Saving...' : 'Save'}</span>
            </button>
            <button
              onClick={handleCancel}
              disabled={isSaving}
              className="flex items-center space-x-2 text-slate hover:text-slate-light transition-colors disabled:opacity-50"
              title="Cancel editing"
            >
              <X className="h-4 w-4" />
              <span>Cancel</span>
            </button>
          </div>
        </div>
        <textarea
          value={editedContent}
          onChange={(e) => setEditedContent(e.target.value)}
          disabled={isSaving}
          className="w-full h-[500px] p-4 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none disabled:opacity-50"
          placeholder="Edit your content..."
        />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-8">
      <div className="flex justify-end space-x-4 mb-4">
        <CopyButton text={content} />
        <button
          onClick={handleExportPDF}
          disabled={isExporting}
          className="flex items-center space-x-2 text-primary hover:text-primary-hover transition-colors disabled:opacity-50"
          title="Export as PDF"
        >
          <FileDown className="h-4 w-4" />
          <span>{isExporting ? 'Exporting...' : 'Export PDF'}</span>
        </button>
        <button
          onClick={() => setIsEditing(true)}
          className="flex items-center space-x-2 text-primary hover:text-primary-hover transition-colors"
          title="Edit content"
        >
          <Edit2 className="h-4 w-4" />
          <span>Edit</span>
        </button>
      </div>
      {error && (
        <div className="mb-4 text-red-600 text-sm">{error}</div>
      )}
      <div className="prose max-w-none whitespace-pre-line">
        {content}
      </div>
    </div>
  );
}